import axios from "axios";
import { atualUfDefinition, EnumAmbienteTipos } from "../../../helpers/utils";

function isLocalhost() {
  const currentHost = window.location.hostname || "";
  return currentHost === "localhost" || currentHost === "127.0.0.1";
}

function resolveBaseUrl() {
  if (isLocalhost()) {
    return process.env.VUE_APP_BASE_API_LOCAL || "http://localhost:64332/";
  }

  if (atualUfDefinition && atualUfDefinition.env) {
    return atualUfDefinition.env;
  }

  return process.env.VUE_APP_BASE_API_CE_HLG || "";
}

export function resolveApiUrl(url) {
  if (!url || /^https?:\/\//i.test(url)) {
    return url;
  }

  const baseUrl = resolveBaseUrl();
  if (!baseUrl) {
    return url;
  }

  return `${baseUrl.replace(/\/$/, "")}/${String(url).replace(/^\//, "")}`;
}

export function isApiArtifactUrl(url) {
  return /^\/api\//i.test(String(url || ""));
}

export async function openApiArtifact(url) {
  const previewWindow = window.open("", "_blank");
  if (previewWindow) {
    previewWindow.opener = null;
    previewWindow.document.title = "Abrindo artefato...";
    previewWindow.document.body.textContent = "Carregando artefato...";
  }

  try {
    const response = await api.get(url, { responseType: "blob" });
    const contentType = String(response.headers["content-type"] || "application/octet-stream")
      .split(";")[0]
      .trim()
      .toLowerCase();
    const disposition = response.headers["content-disposition"] || "";
    const fileName = resolveDownloadFileName(disposition, url);
    const blob = new Blob([response.data], { type: contentType });
    const blobUrl = window.URL.createObjectURL(blob);

    if (previewWindow && canPreviewArtifact(contentType, fileName)) {
      previewWindow.location.replace(blobUrl);
      window.setTimeout(() => window.URL.revokeObjectURL(blobUrl), 300000);
      return;
    }

    if (previewWindow) {
      previewWindow.close();
    }
    downloadBlob(blobUrl, fileName);
  } catch (error) {
    if (previewWindow) {
      previewWindow.close();
    }
    throw error;
  }
}

function downloadBlob(blobUrl, fileName) {
  const link = document.createElement("a");
  link.href = blobUrl;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.setTimeout(() => window.URL.revokeObjectURL(blobUrl), 1000);
}

function canPreviewArtifact(contentType, fileName) {
  if (/^(text\/|image\/|audio\/|video\/)/i.test(contentType)) return true;
  if (/^application\/(pdf|json|xml|xhtml\+xml)$/i.test(contentType)) return true;

  return /\.(pdf|txt|html?|json|xml|csv|png|jpe?g|gif|webp|svg|mp3|wav|ogg|mp4|webm)$/i.test(
    String(fileName || "")
  );
}

function resolveDownloadFileName(disposition, url) {
  const encodedMatch = /filename\*=UTF-8''([^;]+)/i.exec(disposition || "");
  if (encodedMatch) {
    try {
      return decodeURIComponent(encodedMatch[1].trim().replace(/"/g, ""));
    } catch {
      return encodedMatch[1].trim().replace(/"/g, "");
    }
  }

  const plainMatch = /filename="?([^";]+)"?/i.exec(disposition || "");
  if (plainMatch) {
    return plainMatch[1].trim();
  }

  const path = String(url || "").split("?")[0].split("/").filter(Boolean).pop();
  return path && path.includes(".") ? path : "ia-ian-artefato.html";
}

const api = axios.create({
  baseURL: resolveBaseUrl(),
});

export function resolveBanco() {
  const params = new URLSearchParams(window.location.search || "");
  const bancoParam = params.get("banco");

  if (bancoParam) {
    localStorage.setItem("iaColetaBanco", bancoParam);
    return bancoParam;
  }

  if (atualUfDefinition && atualUfDefinition.banco) {
    return atualUfDefinition.banco;
  }

  if (isLocalhost()) {
    return "ce-sp-hlg";
  }

  const bancoStorage = localStorage.getItem("iaColetaBanco");
  if (bancoStorage) {
    return bancoStorage;
  }

  return "";
}

export function resolveBranch() {
  const params = new URLSearchParams(window.location.search || "");
  const branchParam = params.get("branch");

  if (branchParam) {
    return branchParam;
  }

  if (atualUfDefinition && atualUfDefinition.ambient === EnumAmbienteTipos.prod) {
    return "main";
  }

  return "hlg";
}

api.interceptors.request.use((config) => {
  const token = localStorage.getItem("token");

  config.headers = config.headers || {};
  config.headers["banco"] = resolveBanco();
  config.headers["branch"] = resolveBranch();

  if (token) {
    config.headers["Authorization"] = `Bearer ${token}`;
  }

  return config;
});

export const iaColetaEvidenciasApi = {
  async obterPainel(params) {
    const { data } = await api.get("/api/IaColetaEvidencias/painel", {
      params,
    });
    return data;
  },

  async obterFiltros() {
    const { data } = await api.get("/api/IaColetaEvidencias/filtros");
    return data;
  },

  async obterResultados(params) {
    const { data } = await api.get("/api/IaColetaEvidencias/resultados", {
      params,
    });
    return data;
  },

  async obterStatus() {
    const { data } = await api.get("/api/IaColetaEvidencias/status");
    return data;
  },

  async listarQuesitosRegras() {
    const { data } = await api.get("/api/IaColetaEvidencias/regras/quesitos");
    return data;
  },

  async listarRegras(params) {
    const { data } = await api.get("/api/IaColetaEvidencias/regras", {
      params,
    });
    return data;
  },

  async salvarRegra(payload) {
    const id = payload && payload.analiseIaUploadRegraId;
    if (id) {
      const { data } = await api.put(`/api/IaColetaEvidencias/regras/${encodeURIComponent(id)}`, payload);
      return data;
    }

    const { data } = await api.post("/api/IaColetaEvidencias/regras", payload);
    return data;
  },

  async desativarRegra(id) {
    const { data } = await api.post(`/api/IaColetaEvidencias/regras/${encodeURIComponent(id)}/desativar`);
    return data;
  },

  async previewComando(payload) {
    const { data } = await api.post("/api/IaColetaEvidencias/comandos/preview", payload);
    return data;
  },

  async executarComando(payload) {
    const { data } = await api.post("/api/IaColetaEvidencias/comandos/executar", payload);
    return data;
  },

  async listarProcessos() {
    const { data } = await api.get("/api/IaColetaEvidencias/comandos/processos");
    return data;
  },

  async cancelarProcesso(id) {
    const { data } = await api.post(`/api/IaColetaEvidencias/comandos/processos/${encodeURIComponent(id)}/cancelar`);
    return data;
  },

  async listarBatchProjetos() {
    const { data } = await api.get("/api/IaColetaEvidencias/batch-projetos");
    return data;
  },

  async criarBatchProjeto(payload) {
    const { data } = await api.post("/api/IaColetaEvidencias/batch-projetos", payload);
    return data;
  },

  async syncBatchProjeto(id) {
    const { data } = await api.post(`/api/IaColetaEvidencias/batch-projetos/${encodeURIComponent(id)}/sync`);
    return data;
  },

  async cancelarBatchProjeto(id) {
    const { data } = await api.post(`/api/IaColetaEvidencias/batch-projetos/${encodeURIComponent(id)}/cancelar`);
    return data;
  },
};
